// useTranscript.ts — stable writers for the open conversation's transcript.
//
// Shared by the session's socket and by the response-streaming fallback
// (see useTurnRouting.ts), so a turn renders identically however its
// frames reached the page. Each writer is a functional update over one of
// the pure reducers in transcript.ts, created once, so handing them to a
// socket or a stream never goes stale.
//
// Also registers the tool-UI sink: a `{"ui": …}` frame on any of a UI's
// channels replaces that UI, and the socket layer has no way to reach
// component state, so it hands the replacement here — the same path a
// fresh `tool_ui` frame takes.
import React, { useCallback, useEffect } from 'react';
import type { SessionMessage } from '../api';
import { setUiSink } from './live/channelStore';
import {
  appendToLastAssistant as appendFragment,
  appendTurnError as appendError,
  openTurnBubbles as openBubbles,
  upsertToolCall as upsertCall,
  upsertToolUi as upsertUi,
} from './transcript';

export interface TranscriptWriters {
  upsertToolCall: (toolCallId: string, patch: Partial<SessionMessage>) => void;
  upsertToolUi: (toolCallId: string, markdown: string) => void;
  appendToLastAssistant: (field: 'content' | 'reasoning', fragment: string) => void;
  appendTurnError: (message: string) => void;
  openTurnBubbles: (prompt: string) => void;
}

export function useTranscript(
  setMessages: React.Dispatch<React.SetStateAction<SessionMessage[]>>,
): TranscriptWriters {
  const upsertToolCall = useCallback(
    (toolCallId: string, patch: Partial<SessionMessage>) => {
      setMessages((prev) => upsertCall(prev, toolCallId, patch));
    },
    [setMessages],
  );

  const upsertToolUi = useCallback((toolCallId: string, markdown: string) => {
    setMessages((prev) => upsertUi(prev, toolCallId, markdown));
  }, [setMessages]);

  useEffect(() => {
    setUiSink(upsertToolUi);
  }, [upsertToolUi]);

  const appendToLastAssistant = useCallback(
    (field: 'content' | 'reasoning', fragment: string) => {
      setMessages((prev) => appendFragment(prev, field, fragment));
    },
    [setMessages],
  );

  const appendTurnError = useCallback((message: string) => {
    console.debug('[sessions] turn error landed in the transcript');
    setMessages((prev) => appendError(prev, message));
  }, [setMessages]);

  const openTurnBubbles = useCallback((prompt: string) => {
    setMessages((prev) => openBubbles(prev, prompt));
  }, [setMessages]);

  return { upsertToolCall, upsertToolUi, appendToLastAssistant, appendTurnError, openTurnBubbles };
}
